import { create } from 'zustand';

import { createBlock } from './factories';
import type { EmailBlock, EmailBlockKind, EmailDocument, EmailTheme } from './types';

const HISTORY_LIMIT = 40;

export type BlockMove = 'up' | 'down';

interface EmailBuilderState {
  /** `null` = kreator jeszcze nie wystartował, pokazujemy wybór szablonu. */
  doc: EmailDocument | null;
  selectedId: string | null;
  past: EmailDocument[];
  future: EmailDocument[];

  start: (doc: EmailDocument) => void;
  close: () => void;
  select: (id: string | null) => void;

  addBlock: (kind: EmailBlockKind, afterId?: string | null) => void;
  updateBlock: (id: string, patch: Partial<EmailBlock>) => void;
  removeBlock: (id: string) => void;
  moveBlock: (id: string, move: BlockMove) => void;
  duplicateBlock: (id: string) => void;

  updateMeta: (patch: Partial<Pick<EmailDocument, 'name' | 'subject' | 'preheader'>>) => void;
  updateTheme: (patch: Partial<EmailTheme>) => void;

  undo: () => void;
  redo: () => void;
}

export const useEmailBuilderStore = create<EmailBuilderState>((set, get) => {
  /**
   * Każda zmiana treści przechodzi tędy: odkłada poprzedni dokument na stos cofania i
   * czyści gałąź ponowienia. Dzięki temu żadna akcja nie musi pamiętać o historii sama.
   */
  const commit = (next: (doc: EmailDocument) => EmailDocument) => {
    const { doc, past } = get();
    if (!doc) return;
    const history = [...past, doc].slice(-HISTORY_LIMIT);
    set({ doc: next(doc), past: history, future: [] });
  };

  const withBlocks = (doc: EmailDocument, blocks: EmailBlock[]): EmailDocument => ({ ...doc, blocks });

  return {
    doc: null,
    selectedId: null,
    past: [],
    future: [],

    start: (doc) => set({
      doc,
      past: [],
      future: [],
      selectedId: doc.blocks.length > 0 ? doc.blocks[0].id : null,
    }),

    close: () => set({ doc: null, selectedId: null, past: [], future: [] }),

    select: (id) => set({ selectedId: id }),

    addBlock: (kind, afterId) => {
      const block = createBlock(kind);
      commit((doc) => {
        const index = afterId ? doc.blocks.findIndex((item) => item.id === afterId) : -1;
        if (index === -1) return withBlocks(doc, [...doc.blocks, block]);
        const blocks = doc.blocks.slice();
        blocks.splice(index + 1, 0, block);
        return withBlocks(doc, blocks);
      });
      set({ selectedId: block.id });
    },

    updateBlock: (id, patch) => commit((doc) => withBlocks(
      doc,
      doc.blocks.map((block) => (block.id === id ? ({ ...block, ...patch } as EmailBlock) : block)),
    )),

    removeBlock: (id) => {
      commit((doc) => withBlocks(doc, doc.blocks.filter((block) => block.id !== id)));
      if (get().selectedId === id) set({ selectedId: null });
    },

    moveBlock: (id, move) => commit((doc) => {
      const index = doc.blocks.findIndex((block) => block.id === id);
      const target = move === 'up' ? index - 1 : index + 1;
      if (index === -1 || target < 0 || target >= doc.blocks.length) return doc;
      const blocks = doc.blocks.slice();
      const [moved] = blocks.splice(index, 1);
      blocks.splice(target, 0, moved);
      return withBlocks(doc, blocks);
    }),

    duplicateBlock: (id) => {
      const source = get().doc?.blocks.find((block) => block.id === id);
      if (!source) return;
      const copy = { ...source, id: `${source.kind}-${Date.now().toString(36)}` } as EmailBlock;
      commit((doc) => {
        const index = doc.blocks.findIndex((block) => block.id === id);
        const blocks = doc.blocks.slice();
        blocks.splice(index + 1, 0, copy);
        return withBlocks(doc, blocks);
      });
      set({ selectedId: copy.id });
    },

    updateMeta: (patch) => commit((doc) => ({ ...doc, ...patch })),

    updateTheme: (patch) => commit((doc) => ({ ...doc, theme: { ...doc.theme, ...patch } })),

    undo: () => {
      const { doc, past, future } = get();
      if (!doc || past.length === 0) return;
      const previous = past[past.length - 1];
      set({ doc: previous, past: past.slice(0, -1), future: [doc, ...future].slice(0, HISTORY_LIMIT) });
    },

    redo: () => {
      const { doc, past, future } = get();
      if (!doc || future.length === 0) return;
      const [next, ...rest] = future;
      set({ doc: next, past: [...past, doc].slice(-HISTORY_LIMIT), future: rest });
    },
  };
});
